import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useSmartLivingSync } from '../hooks/useSmartLivingSync';
import SEO from '../components/SEO';
import BreathingExercise from '../components/BreathingExercise';
import InstagramFeed from '../components/InstagramFeedNew';
import SelfAssessmentQuiz from '../components/SelfAssessmentQuiz';
import TestimonialsCarousel from '../components/TestimonialsCarousel';

const HomePage = () => {
    const { getAllArticles } = useSmartLivingSync();
    const { t } = useTranslation();
    
    // Get latest articles from all sources
    const allArticles = getAllArticles().slice(0, 3);

    return (
        <>
            <SEO 
                title="Psihoterapeut Oana Tenea - Consiliere și Suport Psihologic București & Online"
                description="Oana Tenea - psihoterapeut cognitiv-comportamental (CBT), specialist Schema Therapy (ISST), EMDR și terapie de cuplu. Cabinet în București (Calea Dorobanți) și consultații online."
                path="/"
            />
            
            <div className="overflow-hidden bg-stone-50 dark:bg-stone-950 text-stone-800 dark:text-stone-200 transition-colors duration-300">
                
                {/* HERO SECTION */}
                <section className="relative min-h-[90vh] flex items-center pt-8 pb-16 lg:py-20 bg-gradient-to-b from-stone-100/80 via-warm-beige/30 to-stone-50 dark:from-stone-900/80 dark:via-stone-900/40 dark:to-stone-950">
                    {/* Ambient subtle backlights */}
                    <div className="absolute top-10 left-1/4 w-96 h-96 bg-terracotta/10 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-amber/10 rounded-full blur-3xl pointer-events-none"></div>

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                            
                            {/* Left Column: Text & Credentials */}
                            <div className="lg:col-span-7 space-y-6 sm:space-y-8 animate-fade-in text-center lg:text-left">
                                
                                {/* Tag Pill */}
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/90 dark:bg-stone-800/90 backdrop-blur-md border border-stone-200/80 dark:border-stone-700 shadow-xs">
                                    <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse"></span>
                                    <span className="text-xs font-semibold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                                        {t('home.tag')}
                                    </span>
                                </div>
                                
                                {/* Impactful Headline */}
                                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-stone-900 dark:text-stone-100 leading-[1.12] tracking-tight">
                                    {t('home.title1')}{' '}
                                    <span className="italic font-normal font-display text-terracotta block sm:inline">
                                        {t('home.title2')}
                                    </span>
                                </h1>
                                
                                {/* Bio Description */}
                                <p className="text-base sm:text-lg lg:text-xl text-stone-600 dark:text-stone-300 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                                    <span className="block sm:hidden">
                                        <Trans i18nKey="home.desc_mobile">
                                            Sunt <strong className="text-stone-900 dark:text-white font-semibold">Oana Tenea</strong>, psihoterapeut certificat. Te ajut cu anxietate, depresie și provocări emoționale.
                                        </Trans>
                                    </span>
                                    <span className="hidden sm:block">
                                        <Trans i18nKey="home.desc_desktop">
                                            Sunt <strong className="text-stone-900 dark:text-white font-semibold">Oana Tenea</strong>, psihoterapeut cu experiență în consiliere psihologică, terapie de cuplu și suport pentru anxietate, depresie și alte provocări emoționale.
                                        </Trans>
                                    </span>
                                </p>
                                
                                {/* Credential Pills */}
                                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 pt-2">
                                    <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-white/80 dark:bg-stone-800/80 border border-stone-200/70 dark:border-stone-700 text-stone-700 dark:text-stone-300 text-xs sm:text-sm font-medium shadow-xs">
                                        <div className="w-5 h-5 rounded-full bg-terracotta/15 flex items-center justify-center text-terracotta flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('home.cred1')}</span>
                                    </div>

                                    <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-white/80 dark:bg-stone-800/80 border border-stone-200/70 dark:border-stone-700 text-stone-700 dark:text-stone-300 text-xs sm:text-sm font-medium shadow-xs">
                                        <div className="w-5 h-5 rounded-full bg-terracotta/15 flex items-center justify-center text-terracotta flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('home.cred2')}</span>
                                    </div>

                                    <div className="hidden sm:flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-white/80 dark:bg-stone-800/80 border border-stone-200/70 dark:border-stone-700 text-stone-700 dark:text-stone-300 text-xs sm:text-sm font-medium shadow-xs">
                                        <div className="w-5 h-5 rounded-full bg-terracotta/15 flex items-center justify-center text-terracotta flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('home.cred3')}</span>
                                    </div>
                                </div>
                                
                                {/* Primary and Secondary CTAs */}
                                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-2">
                                    <Link 
                                        to="/contact"
                                        className="inline-flex items-center justify-center px-8 py-4 bg-terracotta hover:bg-terracotta-dark text-white font-semibold rounded-full shadow-warm hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 text-base"
                                    >
                                        <span>{t('home.btn_book')}</span>
                                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </Link>
                                    <Link 
                                        to="/despre"
                                        className="inline-flex items-center justify-center px-8 py-4 bg-white dark:bg-stone-800 text-stone-800 dark:text-stone-200 hover:text-terracotta font-semibold rounded-full border border-stone-300/80 dark:border-stone-700 shadow-soft hover:shadow-medium transition-all duration-200 text-base"
                                    >
                                        {t('home.btn_about')}
                                    </Link>
                                </div>
                            </div>

                            {/* Right Column: Hero Portrait in Elegant Framing */}
                            <div className="lg:col-span-5 flex justify-center relative">
                                <div className="relative w-full max-w-sm sm:max-w-md">
                                    
                                    {/* Decorative Aura / Frame Background */}
                                    <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-tr from-terracotta/20 via-amber-400/10 to-sage/20 transform rotate-3 scale-105 filter blur-lg"></div>

                                    {/* Main Portrait Container */}
                                    <div className="relative rounded-[2.5rem] overflow-hidden bg-stone-200 dark:bg-stone-800 shadow-elevated border-4 border-white dark:border-stone-800 aspect-[4/5]">
                                        <img 
                                            src="/3DX_2399web.jpg" 
                                            alt="Oana Tenea - Psihoterapeut Cognitiv-Comportamental"
                                            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                                            loading="eager"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = '/OanaTenea.png';
                                            }}
                                        />

                                        {/* Gentle bottom gradient for depth */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent"></div>

                                        {/* Bottom Overlay Label */}
                                        <div className="absolute bottom-5 left-5 right-5 text-white">
                                            <div className="font-display text-xl font-bold">Oana Tenea</div>
                                            <div className="text-xs text-stone-200 font-medium">Cabinet Calea Dorobanți & Online</div>
                                        </div>
                                    </div>

                                    {/* Floating Trust Chip 1: Top Right */}
                                    <div className="absolute -top-4 -right-4 sm:-right-6 bg-white/95 dark:bg-stone-800/95 backdrop-blur-md p-3 sm:p-4 rounded-2xl shadow-elevated border border-stone-200/80 dark:border-stone-700 max-w-[200px] hidden sm:block animate-float">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                                            <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400">Certificare</span>
                                        </div>
                                        <p className="text-xs font-semibold text-stone-800 dark:text-stone-100">
                                            Membru CPR & ISST Schema Therapy
                                        </p>
                                    </div>

                                    {/* Floating Trust Chip 2: Bottom Left */}
                                    <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-white/95 dark:bg-stone-800/95 backdrop-blur-md p-3 sm:p-4 rounded-2xl shadow-elevated border border-stone-200/80 dark:border-stone-700 hidden sm:block">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-xs">
                                                CBT
                                            </div>
                                            <div>
                                                <div className="text-xs font-bold text-stone-900 dark:text-stone-100">Abordare Validată</div>
                                                <div className="text-[11px] text-stone-500 dark:text-stone-400">EMDR • Schema • EFT</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                {/* TRUST & SPECIALTIES SUMMARY STRIP */}
                <section className="py-12 bg-white dark:bg-stone-900 border-y border-stone-200/70 dark:border-stone-800 transition-colors duration-300">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                            
                            <div className="p-4 rounded-2xl hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors">
                                <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta mx-auto mb-3 flex items-center justify-center">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <h3 className="font-bold text-stone-900 dark:text-stone-100 text-sm sm:text-base">Anxietate & Panică</h3>
                                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">Tehnici CBT & Schema Therapy</p>
                            </div>

                            <div className="p-4 rounded-2xl hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors">
                                <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta mx-auto mb-3 flex items-center justify-center">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                </div>
                                <h3 className="font-bold text-stone-900 dark:text-stone-100 text-sm sm:text-base">Terapie de Cuplu</h3>
                                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">Conexiune emoțională prin EFT</p>
                            </div>

                            <div className="p-4 rounded-2xl hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors">
                                <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta mx-auto mb-3 flex items-center justify-center">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <h3 className="font-bold text-stone-900 dark:text-stone-100 text-sm sm:text-base">Traumă & Doliu</h3>
                                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">Procesare ghidată prin EMDR</p>
                            </div>

                            <div className="p-4 rounded-2xl hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors">
                                <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta mx-auto mb-3 flex items-center justify-center">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                                <h3 className="font-bold text-stone-900 dark:text-stone-100 text-sm sm:text-base">Binge Eating & Mood</h3>
                                <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">Gestionare tulburări alimentare</p>
                            </div>

                        </div>
                    </div>
                </section>

                {/* BREATHING EXERCISE SECTION */}
                <section className="py-24 bg-gradient-to-b from-stone-50 via-warm-beige/30 to-stone-100/50 dark:from-stone-950 dark:via-stone-900 dark:to-stone-950">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
                        <div className="text-center mb-14 max-w-2xl mx-auto">
                            <span className="inline-block px-3.5 py-1.5 bg-sage-100 dark:bg-stone-800 text-sage-800 dark:text-sage-300 rounded-full font-semibold text-xs tracking-wider uppercase mb-4 border border-sage-200 dark:border-stone-700">
                                {t('home.breathe_tag')}
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 dark:text-stone-100 mb-4 tracking-tight">
                                {t('home.breathe_title')}
                            </h2>
                            <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg leading-relaxed">
                                {t('home.breathe_desc')}
                            </p>
                        </div>

                        <div className="flex justify-center">
                            <div className="w-full max-w-2xl">
                                <BreathingExercise />
                            </div>
                        </div>
                    </div>
                </section>

                {/* FEATURED ARTICLES & SMARTLIVING COLLABORATION */}
                <section className="py-24 bg-white dark:bg-stone-900 border-t border-stone-200/70 dark:border-stone-800">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                        
                        {/* Section Header */}
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-semibold uppercase tracking-wider mb-4 border border-stone-200 dark:border-stone-700">
                                <svg className="w-4 h-4 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                </svg>
                                <span>Publicații & Expertiză</span>
                            </div>

                            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 dark:text-stone-100 mb-4 tracking-tight">
                                {t('home.blog_title')}
                                <span className="italic font-normal font-display text-terracotta"> {t('home.blog_title_highlight')}</span>
                            </h2>
                            <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg leading-relaxed mb-8">
                                {t('home.blog_desc')}
                            </p>

                            {/* SmartLiving Partner Banner */}
                            <div className="inline-flex flex-col sm:flex-row items-center gap-3 p-4 px-6 rounded-2xl bg-gradient-to-r from-stone-50 to-amber-50/50 dark:from-stone-800 dark:to-stone-800/50 border border-stone-200 dark:border-stone-700 text-left max-w-2xl mx-auto">
                                <div className="w-10 h-10 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center flex-shrink-0">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="font-semibold text-stone-900 dark:text-stone-100 text-sm">
                                        {t('home.blog_collab')}
                                    </div>
                                    <div className="text-xs text-stone-500 dark:text-stone-400">
                                        {t('home.blog_collab_desc')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Articles Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-14">
                            {allArticles.map((post, index) => {
                                const isExternalArticle = post.source === 'SmartLiving.ro';
                                const LinkComponent = isExternalArticle ? 'a' : Link;
                                const linkProps = isExternalArticle 
                                    ? { href: post.externalUrl, target: '_blank', rel: 'noopener noreferrer' }
                                    : { to: `/blog/${post.slug || post.newSlug}` };

                                return (
                                    <article 
                                        key={post.id} 
                                        className="group bg-stone-50/70 dark:bg-stone-800/60 rounded-3xl p-8 border border-stone-200/80 dark:border-stone-700/80 shadow-soft hover:shadow-warm transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between"
                                    >
                                        <div>
                                            {/* Meta Top: Category & Reading Time */}
                                            <div className="flex items-center justify-between gap-2 mb-6">
                                                <div className="flex items-center gap-2">
                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white dark:bg-stone-700 text-stone-700 dark:text-stone-200 border border-stone-200 dark:border-stone-600 shadow-2xs">
                                                        {post.category}
                                                    </span>
                                                    {isExternalArticle && (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                                            SmartLiving
                                                        </span>
                                                    )}
                                                </div>

                                                <span className="text-xs text-stone-500 dark:text-stone-400 font-medium flex items-center">
                                                    <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                    </svg>
                                                    {post.readingTime} min
                                                </span>
                                            </div>

                                            {/* Title */}
                                            <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100 mb-4 group-hover:text-terracotta transition-colors leading-snug line-clamp-2">
                                                {post.title}
                                            </h3>

                                            {/* Excerpt */}
                                            <p className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed mb-6 line-clamp-3">
                                                {post.excerpt || post.metaDescription}
                                            </p>
                                        </div>

                                        {/* Action link */}
                                        <div className="pt-4 border-t border-stone-200/60 dark:border-stone-700/60">
                                            <LinkComponent 
                                                {...linkProps}
                                                className="inline-flex items-center text-sm font-semibold text-terracotta group-hover:text-terracotta-dark group-hover:translate-x-1 transition-all duration-200"
                                            >
                                                <span>
                                                    {isExternalArticle ? t('home.blog_read_smartliving') : t('home.blog_read_more')}
                                                </span>
                                                <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                                </svg>
                                            </LinkComponent>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        {/* All Articles CTA */}
                        <div className="text-center">
                            <Link 
                                to="/blog"
                                className="inline-flex items-center px-8 py-3.5 bg-stone-100 dark:bg-stone-800 text-stone-800 dark:text-stone-200 hover:text-terracotta font-semibold rounded-full border border-stone-300/80 dark:border-stone-700 shadow-soft hover:shadow-warm transition-all duration-200 text-sm sm:text-base"
                            >
                                <span>{t('home.blog_btn_all')}</span>
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* SELF ASSESSMENT SECTION */}
                <section className="py-24 bg-gradient-to-b from-stone-50 via-warm-beige/30 to-stone-100/50 dark:from-stone-950 dark:via-stone-900 dark:to-stone-950 border-t border-stone-200/70 dark:border-stone-800">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
                        <div className="text-center mb-14 max-w-2xl mx-auto">
                            <span className="inline-block px-3.5 py-1.5 bg-terracotta-50 dark:bg-stone-800 text-terracotta dark:text-terracotta-light rounded-full font-semibold text-xs tracking-wider uppercase mb-4 border border-terracotta-200/60 dark:border-stone-700">
                                {t('home.quiz_tag')}
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-stone-900 dark:text-stone-100 mb-4 tracking-tight">
                                {t('home.quiz_title')}
                            </h2>
                            <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg leading-relaxed">
                                {t('home.quiz_desc')}
                            </p>
                        </div>

                        <div className="flex justify-center">
                            <SelfAssessmentQuiz />
                        </div>
                    </div>
                </section>

                {/* TESTIMONIALS CAROUSEL */}
                <TestimonialsCarousel />

                {/* INSTAGRAM FEED SECTION */}
                <InstagramFeed />

                {/* PRIMARY REASSURANCE CTA BANNER */}
                <section className="py-24 bg-gradient-to-br from-terracotta via-warm-orange to-terracotta-dark text-white relative overflow-hidden">
                    {/* Ambient pattern */}
                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                        <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                            <defs>
                                <pattern id="home-cta-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                    <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                                </pattern>
                            </defs>
                            <rect width="100" height="100" fill="url(#home-cta-pattern)" />
                        </svg>
                    </div>

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 max-w-4xl">
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
                            {t('home.cta_title1')}{' '}
                            <span className="italic block sm:inline font-normal">{t('home.cta_title2')}</span>
                        </h2>
                        <p className="text-lg sm:text-xl text-white/90 mb-10 leading-relaxed max-w-2xl mx-auto">
                            {t('home.cta_desc')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link 
                                to="/contact"
                                className="px-9 py-4 bg-white text-terracotta-dark font-bold rounded-full hover:bg-stone-50 transition-all duration-200 shadow-elevated transform hover:-translate-y-0.5 text-base"
                            >
                                {t('home.cta_btn_book')}
                            </Link>
                            <Link 
                                to="/servicii"
                                className="px-9 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-colors duration-200 text-base"
                            >
                                {t('home.cta_btn_services')}
                            </Link>
                        </div>
                    </div>
                </section>

            </div>
        </>
    );
};

export default HomePage;
